import { Injectable } from '@angular/core'
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  firstValueFrom
} from 'rxjs'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { ICreateUser, IUpdatedUser, IUser } from './user.interface'
import { IUpdateDTModel } from '../helpers/update-model.interface'
import { HttpErrorHandlerService } from '../helpers/http-error-handler.service'
import { IAppPluginWidget } from '../app-plugin-widget/app-plugin-widget.interface'
import { Observable } from 'rxjs/internal/Observable'
import { API_ENDPOINT_PATH, API_HTTP_HEADERS } from "../helpers/api-constant";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  readonly #headers: HttpHeaders
  readonly #urlEndpoint: string
  private readonly defaultUser = {
    _id: '-1',
    active_dashboard: '-1',
    app_dashboards: [],
    auth: [],
    display_name: 'dummy.user',
    enabled: false,
    login_name: 'dummy.user',
    roles: []
  }

  private readonly userSubject$: BehaviorSubject<IUser> = new BehaviorSubject<IUser>(this.defaultUser)
  private readonly usersSubject$: BehaviorSubject<IUser[]> = new BehaviorSubject<IUser[]>([])

  private readonly user$: Observable<IUser> = this.userSubject$.asObservable()
    .pipe(
      filter((user: IUser): user is IUser => user._id !== '-1'),
      finalize(() => console.log('user$ - Observable completed.'))
    )
  private readonly users$: Observable<IUser[]> = this.usersSubject$.asObservable()

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerService: HttpErrorHandlerService
  ) {
	  this.#urlEndpoint = `${API_ENDPOINT_PATH}/user`;
	  this.#headers = new HttpHeaders(API_HTTP_HEADERS);
  }

  private getUrl(): string {
    return `${this.#urlEndpoint}`
  }

  private getHeader(): HttpHeaders {
    return this.#headers
  }

  getUser(): IUser {
    return this.userSubject$.getValue()
  }

  setUser(user: IUser): void {
    this.userSubject$.next(user)
  }

  observeUser$(): Observable<IUser> {
    return this.user$
  }

  updateUsers(users: IUser[]): void {
    this.usersSubject$.next(users)
  }

  observeAllUsers$(): Observable<IUser[]> {
    return this.users$;
  }

  async upsertOne(payload: IUser|ICreateUser|IUpdatedUser): Promise<{}|IUser|IUpdateDTModel> {
    const result = this.http.post<IUser>(`${this.getUrl()}`, payload,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async getById(id: string, attr?: string): Promise<{}|IUser> {
    let queryUrl = `${this.getUrl()}/${id}`
    if (attr) { queryUrl += `?attr=${attr}`}

    const result = this.http.get<IUser>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async listAll(params?: string): Promise<IUser[]> {
    let queryUrl = `${this.getUrl()}`
    if (params) { queryUrl += `?${params}` }

    const result = this.http.get<IUser[]>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
    return await firstValueFrom(result)
  }

  async deleteById(id: string): Promise<void> {
	  const headers = this.getHeader().delete('Content-Type').delete('content-type');

    const result = this.http.delete(`${this.getUrl()}/${id}`,
      { headers: headers }).pipe(catchError(this.errorHandlerService.handleError))
    await firstValueFrom(result)
  }

  async listAllAppPluginWidgets(id: string): Promise<IAppPluginWidget[]> {
    let queryUrl = `${this.getUrl()}/${id}/appPluginWidget`
    const result = this.http.get<IAppPluginWidget[]>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
    return await firstValueFrom(result)
  }

  manuallyCompleteUser$(): void {
    this.userSubject$.complete();
  }
}
